"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, type KeyboardEvent, type RefObject } from "react";
import { ArrowDownUp, Check, ChevronDown } from "lucide-react";
import { Popover } from "../ui";
import "./browse.css";
type SortOption<T extends string> = {
  key: T;
  label: string;
};

/**
 * The open panel's items. Mounted only while the panel is open (Popover
 * renders its children then), so the effect puts focus on the chosen order
 * each time it opens, as a native select does, and hands it back to the
 * trigger when the panel closes with focus left nowhere.
 *
 * Keyboard: ↑/↓/Home/End move between the items, Enter/Space choose.
 * Escape and an outside click close (Popover's own handling), and so does a
 * choice — Popover closes on any click inside its panel.
 */
function SortList(t0) {
  const $ = _c(17);
  const {
    value,
    options,
    onChoose,
    label,
    trigger
  } = t0;
  const list = useRef(null);
  let t1;
  if ($[0] !== trigger.current) {
    t1 = () => {
      list.current?.querySelector("[aria-checked=\"true\"]")?.focus({
        preventScroll: true
      });
      const button = trigger.current;
      return () => {
        if (!document.activeElement || document.activeElement === document.body) {
          button?.focus({
            preventScroll: true
          });
        }
      };
    };
    $[0] = trigger.current;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== trigger) {
    t2 = [trigger];
    $[2] = trigger;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  const onKeyDown = _temp;
  let t3;
  if ($[4] !== label) {
    t3 = <span className="v2-bx-sort-head" aria-hidden={true}>{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onChoose || $[7] !== options || $[8] !== value) {
    let t5;
    if ($[10] !== onChoose || $[11] !== value) {
      t5 = o => <button key={o.key} type="button" role="menuitemradio" aria-checked={o.key === value} className="v2-bx-sort-item v2-focus" onClick={() => onChoose(o.key)}><span>{o.label}</span>{o.key === value ? <Check aria-hidden={true} /> : null}</button>;
      $[10] = onChoose;
      $[11] = value;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = options.map(t5);
    $[6] = onChoose;
    $[7] = options;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[13] !== label || $[14] !== t3 || $[15] !== t4) {
    t5 = <div ref={list} role="group" aria-label={label} className="v2-bx-sort-list" onKeyDown={onKeyDown}>{t3}{t4}</div>;
    $[13] = label;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/**
 * THE v2 SORT MENU — replaces the native <select>, whose closed state and
 * option list are drawn by the browser and so never took the system's look.
 * A trigger that names the current order, and a panel of radio items with a
 * gold tick and spine on the chosen one.
 */
function _temp(event) {
  const items = Array.from(event.currentTarget.querySelectorAll("[role=menuitemradio]"));
  const at = items.indexOf(document.activeElement as HTMLButtonElement);
  const move = {
    ArrowDown: at + 1,
    ArrowUp: at - 1,
    Home: 0,
    End: items.length - 1
  };
  if (!(event.key in move)) {
    return;
  }
  event.preventDefault();
  items[(move[event.key] + items.length) % items.length]?.focus();
}
export default function SortMenu(t0) {
  const $ = _c(19);
  const {
    value,
    options,
    onChange,
    label,
    align: t1
  } = t0;
  const align = t1 === undefined ? "end" : t1;
  let t2;
  if ($[0] !== options || $[1] !== value) {
    t2 = options.find(o => o.key === value) ?? options[0];
    $[0] = options;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const current = t2;
  const triggerRef = useRef(null);
  let t3;
  if ($[3] !== current?.label || $[4] !== label) {
    t3 = t4 => {
      const {
        open,
        toggle,
        id
      } = t4;
      return <button ref={triggerRef} type="button" className="v2-bx-trigger v2-bx-sort v2-focus" data-open={open || undefined} onClick={toggle} aria-expanded={open} aria-controls={id} aria-haspopup="menu"><ArrowDownUp aria-hidden={true} /><span className="v2-bx-sort-current"><span className="v2-sr-only">{label}: </span>{current?.label}</span><ChevronDown aria-hidden={true} /></button>;
    };
    $[3] = current?.label;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onChange || $[7] !== value) {
    t4 = key => {
      if (key !== value) {
        onChange(key);
      }
    };
    $[6] = onChange;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== label || $[10] !== options || $[11] !== t4 || $[12] !== value) {
    t5 = <SortList value={value} options={options} label={label} trigger={triggerRef} onChoose={t4} />;
    $[9] = label;
    $[10] = options;
    $[11] = t4;
    $[12] = value;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== align || $[15] !== label || $[16] !== t3 || $[17] !== t5) {
    t6 = <Popover align={align} side="auto" avoidViewportBottom={88} label={label} className="v2-bx-sort-panel" trigger={t3}>{t5}</Popover>;
    $[14] = align;
    $[15] = label;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}